import { Grid, Paper, Stack, TextInput, Tooltip } from '@mantine/core'
import { Button, Group, Text, Title } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetCurrentSponsorPlaceholdersResponse } from '@/services/api/api.types'

import { ConfirmChangesModal } from './ConfirmChangesModal'
import { DisabledTerminology, RequiredTerminology } from './TerminologyContent'
import { TerminologyLabel } from './TerminologyLabel'

interface TerminologyPanelProps {
  showTutorialText: boolean
  registerUnsavedChangesChecker: (tabKey: 'terminology', checker: () => boolean, onDiscard: () => void) => void
}

export function TerminologyPanel({ showTutorialText = false, registerUnsavedChangesChecker }: TerminologyPanelProps) {
  const { api } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [isPending, setIsPending] = useState(false)

  const placeholderTerminologyQuery = useQuery({
    queryKey: ['terminology', 'placeholder'],
    queryFn: () => api.getPlaceholderTerminology(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const [originalTerms, setOriginalTerms] = useState<GetCurrentSponsorPlaceholdersResponse | null>()
  const [terms, setTerms] = useState<GetCurrentSponsorPlaceholdersResponse | null>()
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const allQueriesCompleted = placeholderTerminologyQuery.isSuccess
  const anyQueryError = placeholderTerminologyQuery.isError
  const isLoading = placeholderTerminologyQuery.isLoading

  useEffect(() => {
    if (placeholderTerminologyQuery.data) {
      setOriginalTerms(placeholderTerminologyQuery.data)
      setTerms(placeholderTerminologyQuery.data)
    }
  }, [placeholderTerminologyQuery.data])

  const getTerm = (id: keyof GetCurrentSponsorPlaceholdersResponse): string => {
    return terms?.[id] ?? ''
  }

  const isDisabledValue = (id: string) => {
    return DisabledTerminology.includes(id)
  }

  const hasChanges = useMemo(() => {
    return JSON.stringify(originalTerms) !== JSON.stringify(terms)
  }, [originalTerms, terms])

  const isValid = useMemo(() => {
    if (terms == null) return false

    // No value can be empty
    return Object.keys(terms).every(key => {
      return terms[key as keyof GetCurrentSponsorPlaceholdersResponse] !== ''
    })
  }, [terms])

  const handleCancel = useCallback(() => {
    setTerms(originalTerms)
  }, [originalTerms])

  // Register unsaved changes checker with parent
  useEffect(() => {
    registerUnsavedChangesChecker('terminology', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  const handleSave = () => {
    if (!isValid) {
      console.warn('handleSave: invalid terms', { terms })
      return
    }
    setShowConfirmModal(true)
  }

  const handleConfirmApply = async () => {
    if (terms == null) {
      console.error('handleConfirmApply: terms is null')
      return
    }

    setIsPending(true)
    await api.updateTerminology(terms)
    setIsPending(false)
    setOriginalTerms(terms)
    setShowConfirmModal(false)
  }

  const handleValueChange = (key: keyof GetCurrentSponsorPlaceholdersResponse, value: string) => {
    setTerms(prev => {
      if (prev == null) return null

      return {
        ...prev,
        [key]: value,
      }
    })
  }

  const handleBlurChange = (key: keyof GetCurrentSponsorPlaceholdersResponse) => {
    if (isDisabledValue(key) && !getTerm(key)) {
      setTerms(prev => {
        if (prev == null) return null

        const newTerms = { ...prev }
        delete newTerms[key]

        return newTerms
      })
    }
  }

  if (isLoading || anyQueryError || !allQueriesCompleted) {
    return (
      <Stack>
        <Paper radius="md" p="lg" shadow="sm" px="xl" bg="var(--mantine-color-gray-0)">
          <Group justify="space-between" align="center" p="md">
            {isLoading && <Text>Loading...</Text>}
            {anyQueryError && <Text>Error loading data</Text>}
            {!allQueriesCompleted && <Text>Waiting for all queries to complete...</Text>}
          </Group>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" mb="md">
          <Title order={isMobile ? 6 : 4} mb={isMobile ? 'md' : undefined}>
            Define Sidekick Terms
          </Title>
          <Group>
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges}>
              Cancel
            </Button>
            <Tooltip label="Save becomes available when you make changes. Save or Cancel any changes.">
              <Button onClick={handleSave} disabled={!isValid || !hasChanges}>
                Save
              </Button>
            </Tooltip>
          </Group>
        </Group>

        {showTutorialText && (
          <Text size="md" c="dimmed" mb="md">
            Define the terms Sidekicks will use in the mobile app when interacting with end users.
          </Text>
        )}

        <Paper radius="md" p="lg" shadow="sm" px="xl">
          <Grid>
            {/* Required Terminology */}
            {RequiredTerminology.map(item => {
              const textInput = (
                <TextInput
                  value={getTerm(item.id)}
                  onChange={e => handleValueChange(item.id, e.target.value)}
                  onFocus={() => isDisabledValue(item.id) && handleValueChange(item.id, '')}
                  onBlur={() => handleBlurChange(item.id)}
                  disabled={isDisabledValue(item.id)}
                  styles={{
                    root: {
                      flex: isMobile ? undefined : 1,
                    },
                    input: {
                      opacity: isDisabledValue(item.id) ? 0.6 : 1,
                    },
                  }}
                />
              )

              return (
                <Grid.Col key={item.id} span={12} mb="sm">
                  {isMobile ? (
                    <Stack gap="xs">
                      <TerminologyLabel item={item} required />
                      {textInput}
                    </Stack>
                  ) : (
                    <Group gap="md" align="center">
                      <TerminologyLabel item={item} required />
                      {textInput}
                    </Group>
                  )}
                </Grid.Col>
              )
            })}
          </Grid>
        </Paper>
      </Paper>

      <ConfirmChangesModal
        title="Are you sure?"
        description="Making changes to the terminology can impact how terms appear throughout the application."
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmApply}
        isLoading={isPending}
      />
    </Stack>
  )
}
